import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlagAgentCart } from '../../redux/features/config/config.selector';
import useAISuggestionActions from '../AISuggestions/useAISuggestionActions.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import Colors from '../../utils/color';

/**
 * PULSE ADVISE (HITL 3.0) — the AI co-pilot's recommended ask, folded into the
 * Pulse strip from the old Action Center: the suggestion text with Accept /
 * Dismiss (tick / cross) on one row.
 *
 * Gated by the agent_cart flag (same master switch as the standalone suggestion
 * surface). Accept / Dismiss and the Alt+A / Alt+D shortcuts drive the shared
 * useAISuggestionActions path. Deliberately omits the old "AI recommends you
 * ask:" label and the reason line; the slash-command reminder now lives in
 * PulseSignals. Renders nothing when there's no active suggestion.
 */
const useStyles = makeStyles((theme: Theme) => ({
  ask: {
    marginTop: theme.spacing(1.25),
    // Breathing room below the ask so it doesn't butt up against the pinned AI
    // status band at the bottom of the Pulse card.
    marginBottom: theme.spacing(1.25),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#eef1fb',
    border: '1px solid #c5cae9',
    borderRadius: '8px',
    padding: theme.spacing(0.875, 1.125),
  },
  // Match the "Welcome" intent pill's text: medium weight, 0.72rem.
  q: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.72rem',
    fontWeight: 500,
    color: Colors.readBack,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  okBtn: {
    backgroundColor: Colors.green,
    color: Colors.white,
    '&:hover': { backgroundColor: Colors.green },
  },
  noBtn: {
    backgroundColor: Colors.white,
    color: '#79808a',
    border: `1px solid ${Colors.alto}`,
  },
}));

const PulseAdvise = () => {
  const classes = useStyles();

  // The co-pilot advise loop is gated by the agent_cart flag, matching the
  // standalone AISuggestions surface.
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  const { activeSuggestion, accept, dismiss } = useAISuggestionActions();
  const showAsk = isAgentCartEnabled && !!activeSuggestion;

  // Alt+A / Alt+D mirror the Accept / Dismiss buttons (shared path via
  // useAISuggestionActions). `event.code` is matched (not `event.key`) so macOS
  // Option-key glyphs don't break the match; capture phase so it fires even
  // while the omnibar is focused. No-ops unless the co-pilot is enabled and a
  // suggestion is showing.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    if (!isAgentCartEnabled || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  useEventListener('keydown', handleKeyDown, window, true);

  if (!showAsk || !activeSuggestion) {
    return null;
  }

  return (
    <div className={classes.ask} data-testid="pulse-advise">
      <span className={classes.q}>{activeSuggestion.title}</span>
      <div className={classes.actions}>
        <IconButton
          className={classes.okBtn}
          size="small"
          title="Accept (Alt+A)"
          aria-label="Accept suggestion"
          onClick={() => accept(activeSuggestion)}
          sx={{ p: 0.5 }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          className={classes.noBtn}
          size="small"
          title="Dismiss (Alt+D)"
          aria-label="Dismiss suggestion"
          onClick={() => dismiss(activeSuggestion)}
          sx={{ p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
};

export default PulseAdvise;
